import { useState, type FormEvent } from 'react';

const footerLinks = {
  Platform: [
    { label: 'Marketplace', href: '#marketplace' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Features', href: '#features' },
  ],
  Solutions: [
    { label: 'Solar Energy', href: '/solar-energy-australia' },
    { label: 'Wind Energy', href: '/wind-energy-solutions' },
    { label: 'Hydrogen Energy', href: '/hydrogen-energy-australia' },
    { label: 'For Homes', href: '/#homes' },
    { label: 'For Businesses', href: '/#businesses' },
  ],
  Company: [
    { label: 'About Us', href: '#home' },
    { label: 'Contact', href: '#contact' },
    { label: 'Privacy Policy', href: '/privacy-policy' },
  ],
};

const socialLinks = [
  { icon: 'ri-linkedin-fill', href: '#', label: 'LinkedIn' },
  { icon: 'ri-twitter-x-fill', href: '#', label: 'Twitter' },
  { icon: 'ri-facebook-fill', href: '#', label: 'Facebook' },
  { icon: 'ri-instagram-line', href: '#', label: 'Instagram' },
];

const enquiryTypes = [
  'Homeowner',
  'Business',
  'Installer',
  'Manufacturer',
  'Finance Provider',
  'Energy Retailer',
  'Other',
];

export default function Footer() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('submitting');
    setFormError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormState('success');
      form.reset();
      setTimeout(() => setFormState('idle'), 3000);
      return;
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('https://formspree.io/f/mvzelbqr', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData,
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed && parsed.ok === true) {
        setFormState('success');
        form.reset();
        setTimeout(() => setFormState('idle'), 4000);
      } else {
        const serverMsg = (parsed as Record<string, unknown> | null)?.error
          ? String((parsed as Record<string, unknown>).error)
          : '';
        setFormError(serverMsg || 'Something went wrong. Please try again.');
        setFormState('error');
        setTimeout(() => setFormState('idle'), 4000);
      }
    } catch {
      setFormError('Network error. Please check your connection and try again.');
      setFormState('error');
      setTimeout(() => setFormState('idle'), 4000);
    }
  };

  return (
    <footer id="contact" className="relative bg-foreground-800 text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-16 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <div className="mb-6">
              <img
                src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/473109dd943942010dc73e384d113dfa.png"
                alt="AK Energy logo"
                className="h-14 md:h-16 w-auto"
                loading="lazy"
              />
            </div>

            <p className="text-sm text-white/60 leading-relaxed mb-4 max-w-sm">
              Australia&rsquo;s trusted renewable energy marketplace. Connecting customers, businesses and renewable energy providers to accelerate the transition towards cleaner energy solutions.
            </p>

            <a href="mailto:akenergyau@gmail.com" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors duration-200 mb-8">
              <i className="ri-mail-line text-base"></i>
              <span>akenergyau@gmail.com</span>
            </a>

            <div className="flex items-center gap-3 mb-10">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  rel="nofollow"
                  className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-white hover:border-white/30 transition-all duration-200"
                >
                  <i className={`${s.icon} text-base`}></i>
                </a>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-8">
              {Object.entries(footerLinks).map(([title, links]) => (
                <div key={title}>
                  <h4 className="text-xs font-semibold tracking-widest uppercase text-white/40 mb-4">{title}</h4>
                  <ul className="space-y-2.5">
                    {links.map((link) => (
                      <li key={link.label}>
                        <a href={link.href} className="text-sm text-white/60 hover:text-white transition-colors duration-200">
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div id="enquiry" className="bg-secondary-500/30 rounded-2xl p-6 md:p-8 border border-secondary-400/20">
              <h3 className="font-heading text-lg font-bold text-white mb-1">Send an Enquiry</h3>
              <p className="text-sm text-white/50 mb-6">Tell us about your needs and we&rsquo;ll connect you with the right solution.</p>

              <form onSubmit={handleSubmit} data-readdy-form="">
                <div className="space-y-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Full Name"
                      required
                      className="w-full px-4 py-3 text-sm bg-white/10 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      required
                      className="w-full px-4 py-3 text-sm bg-white/10 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      className="w-full px-4 py-3 text-sm bg-white/10 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all"
                    />
                  </div>
                  <div>
                    <select
                      name="enquiry_type"
                      required
                      defaultValue=""
                      className="w-full px-4 py-3 text-sm bg-white/10 border border-white/10 rounded-xl text-white/80 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all appearance-none"
                      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff80' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                    >
                      <option value="" disabled className="text-foreground-800">I am a...</option>
                      {enquiryTypes.map((t) => (
                        <option key={t} value={t} className="text-foreground-800">{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="Tell us about your energy needs..."
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-3 text-sm bg-white/10 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all resize-none"
                    />
                    <p className="text-xs text-white/30 mt-1 text-right">Max 500 characters</p>
                  </div>

                  <input type="text" name="phone_alt" className="form-hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="whitespace-nowrap w-full px-6 py-3.5 text-sm font-semibold rounded-xl bg-primary-500 text-white hover:bg-primary-400 disabled:opacity-50 transition-all duration-200 cursor-pointer"
                  >
                    {formState === 'submitting' ? 'Sending...' : 'Send Enquiry'}
                  </button>
                </div>

                {formState === 'success' && (
                  <p className="mt-4 text-sm text-primary-400 font-medium text-center">Thank you! Your enquiry has been sent. We&rsquo;ll be in touch soon.</p>
                )}
                {formState === 'error' && formError && (
                  <p className="mt-4 text-sm text-red-400 text-center">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">&copy; {new Date().getFullYear()} AK Energy. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a href="/privacy-policy" className="text-xs text-white/40 hover:text-white/70 transition-colors">Privacy Policy</a>
            <a href="/terms-of-service" className="text-xs text-white/40 hover:text-white/70 transition-colors">Terms of Service</a>
            <a href="/cookie-policy" className="text-xs text-white/40 hover:text-white/70 transition-colors">Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}